import type { GeoJsonDataType } from './types';
import { DataPoint } from './DataPoint';
import { DataSeries } from './DataSeries';
import { DataPrimitive, DataType, IDataSeries, TypedValue } from './DataPrimitive';
import { inferDataTypeFromDataPoints, getDataTypeForPoint, getDataTypeForMeta } from './utils/types';
import { ColumnVal } from './DataFrame';

/**
 * DataSeriesLite class and associated DataSeries selectors
 * @implements {DataPrimitive}
 */
export class DataSeriesLite<T extends DataType = DataType>
    extends DataSeries
    implements DataPrimitive<T>, IDataSeries<T>
{
    public get points(): DataPoint<T>[] {
        this.values.forEach((rawValue, index) => {
            if (!this.internalPoints[index]) {
                this.createDataPointFromValueAtIndex(index);
            }
        });
        return this.internalPoints;
    }

    internalPoints: DataPoint<T>[] = [];

    private values: ColumnVal[];

    /**
     *
     * @param {string} type user explicitly sets the data type
     * @param {string} field or column name
     * @param {ColumnVal} the raw values
     */
    constructor(values: ColumnVal[], type: string, field: string) {
        super([], type, field);
        this.values = values;
        const dataTypeForMeta = getDataTypeForMeta({ name: field, type });
        if (dataTypeForMeta !== 'unknown') {
            this.type = dataTypeForMeta;
        }
    }

    /**
     * Return first dataPoint in series.
     * @public
     * @returns {DataPoint}
     */
    firstPoint(): DataPoint<T> {
        if (!this.internalPoints[0]) {
            this.createDataPointFromValueAtIndex(0);
        }
        return this.internalPoints[0];
    }

    /**
     * Return last dataPoint in series.
     * @public
     * @returns {DataPoint}
     */
    lastPoint(): DataPoint<T> {
        if (!this.internalPoints[this.values.length - 1]) {
            this.createDataPointFromValueAtIndex(this.values.length - 1);
        }
        return this.internalPoints[this.internalPoints.length - 1];
    }

    /**
     * Finds dataPoint(s) in DataSeries by index(es).
     * @public
     * @param {...number} indexes
     * @returns {DataSeries}
     */
    pointsByIndexes(...indexes: number[]): DataSeries<T> {
        const indexedPoints: DataPoint<T>[] = [];
        indexes.forEach(index => {
            let dp = this.internalPoints[index];
            if (!dp) {
                dp = this.createDataPointFromValueAtIndex(index);
            }
            if (dp != null) {
                indexedPoints.push(dp);
            }
        });
        return new DataSeries(indexedPoints);
    }

    /**
     * Finds and returns the individual dataPoint at the given index.
     * @public
     * @param {number} index
     * @returns {DataPoint}
     */
    pointByIndex(indexIn: number): DataPoint<T> {
        let index = indexIn;
        if (index < 0) {
            index = this.values.length + index;
        }
        if (index < 0 || index >= this.values.length) {
            // will handle out-of-range indexes for the time being by returning null
            return null;
        }
        let dp = this.internalPoints[index];
        if (!dp) {
            dp = this.createDataPointFromValueAtIndex(index);
        }
        return dp;
    }

    /**
     * Finds the delta between the last point and point at the given index.
     * A negative index can be used, indicating an offset from the end of the sequence.
     * @public
     * @param {number} index
     * @returns {DataPoint}
     */
    delta(index: number): DataPoint<T> {
        const dp1 = this.lastPoint();
        if (dp1 === undefined) {
            return undefined;
        }
        const { field } = dp1;

        const { type, value: val1 } = dp1.getValue();
        if (type !== 'number') {
            // eslint-disable-next-line no-console
            console.warn(`DSL delta cannot be computed for non-numerical data series of type ${type}`); // Throw an error and catch in pipeline executor?
        }

        let dp2 = null;
        // use undefined as default, because null values will be coerced to 0 (thus invalid indexes return a delta of 0)
        // for aggregate SPL queries like `stats count`, we want no trend value rather than 0 as the trend value
        let delta;
        if (index >= 0) {
            dp2 = this.internalPoints[index];
            if (!dp2) {
                dp2 = this.createDataPointFromValueAtIndex(index);
            }
        } else {
            const newIndex = this.values.length + index;
            dp2 = this.internalPoints[newIndex];
            if (!dp2) {
                dp2 = this.createDataPointFromValueAtIndex(newIndex);
            }
        }
        if (dp2 == null) {
            // eslint-disable-next-line no-console
            console.warn('DSL delta cannot be computed, no data - invalid index'); // Throw an error and catch in pipeline executor?
        } else {
            const { value: val2 } = dp2.getValue();
            delta = val1 - val2;
        }
        if (Number.isNaN(delta)) {
            delta = undefined;
        }
        return new DataPoint(field, { type, value: delta }); // create a new data point since we don't want meta data to carry over
    }

    /**
     * Sets all the values in the Data Series to a static TypedValue.
     * @param {TypedValue} v
     */
    setValue(v: TypedValue<T>) {
        this.internalPoints.forEach(p => {
            p.setValue(v);
        });
        this.values.fill(v.value);
        this.type = inferDataTypeFromDataPoints(this.points) as DataType;
    }

    /**
     * Gets all the values + their type in the Data Series.
     * @returns {TypedValue[]}
     */
    getValue(): TypedValue<T>[] {
        const values: TypedValue<T>[] = [];
        this.points.forEach(p => {
            values.push(p.getValue());
        });
        return values;
    }

    /**
     * Gets all the values (only) in the Data Series.
     * @returns {array}
     */
    getRawValue(): (string | number | GeoJsonDataType)[] {
        return this.values as (string | number | GeoJsonDataType)[];
    }

    private createDataPointFromValueAtIndex(index): DataPoint<T> {
        const dataType: DataType = getDataTypeForPoint(this.values[index], {
            name: this.field,
            type: this.type,
        });
        this.internalPoints[index] = new DataPoint<T>(String(this.field), {
            type: dataType as T,
            value: this.values[index],
        });
        return this.internalPoints[index];
    }
}
